import CustomModal from './component/custom_modal/CustomModal';
import ErrorBoundary from './component/ErrorBoundary';
import SetOrgPassword from './component/login/forgot_password/SetOrgPassword';
import Maintenance from './component/maintenance/Maintenance';
import NonMfaSubmittedEnquiry from './component/nonMfaEnquirySubmitted/NonMfaSubmittedEnquiry';
import i18n, {loadModuleTxFile} from './i18n';
import oktaConfig from './okta-config';
import {
  otpAction,
  userIdAction,
  contractSummaryAction,
  customerDetailsAction,
  machineHealthDetailsAction,
  userNameAction,
} from './redux/actions/actions';
import {appStore} from './redux/createStore';
import SecureRoute from './routes/SecureRoute';
import {postAPICall} from './services/apiCallsHandler';
import log, {setDebugLogging} from './utils/logger';
import {initializeSmartlook} from './utils/smartlookConfig';

import loadable from '@loadable/component';
import {OktaAuth, toRelativeUrl} from '@okta/okta-auth-js';
import {Security, LoginCallback} from '@okta/okta-react';
import {useFlag, useVariant} from '@unleash/proxy-client-react';
import React, {Suspense} from 'react';
import Helmet from 'react-helmet';
import {BrowserRouter, Route, Routes, useNavigate} from 'react-router-dom';

import './style/app.css';

let states;
appStore.subscribe(() => {
  states = appStore.getState().mfaStates;
});

const LockedUserPage = loadable(
  async () => {
    return await import('./component/locked_user_page/LockedUserPage');
  },
  {fallback: <Suspense fallback="Loading"> </Suspense>}
);

const FAQ = loadable(
  async () => {
    return await import('./component/login/faq/FAQ');
  },
  {fallback: <Suspense fallback="Loading"> </Suspense>}
);
const MobileVerification = loadable(
  async () => {
    return await import('./component/MobileVerification/MobileVerification');
  },
  {fallback: <Suspense fallback="Loading"> </Suspense>}
);
const OffersLanding = loadable(
  async () => {
    return await import(
      './component/offers/outside_mfa_offers_landing/NonMfaOffersListLanding'
    );
  },
  {fallback: <Suspense fallback="Loading"> </Suspense>}
);

const MfaPreviousOffersLanding = loadable(
  async () => {
    return await import(
      './component/offers/inside_mfa_offers_landing/MfaPreviousOffersLanding'
    );
  },
  {fallback: <Suspense fallback="Loading"> </Suspense>}
);

const ResaleLanding = loadable(
  async () => {
    return await import('./component/resale/resaleLandingPage');
  },
  {fallback: <Suspense fallback="Loading"> </Suspense>}
);

const MachineHealth = loadable(
  async () => {
    return await import('./component/machineHealth/MachineHealth');
  },
  {fallback: <Suspense fallback="Loading"> </Suspense>}
);

const MachineInsightsDetails = loadable(
  async () => {
    return await import('./component/machineHealth/MachineInsightsDetails');
  },
  {fallback: <Suspense fallback="Loading"> </Suspense>}
);

const UsageScoreDetails = loadable(
  async () => {
    return await import('./component/machineHealth/UsageScoreDetails');
  },
  {fallback: <Suspense fallback="Loading"> </Suspense>}
);

const MaintenanceScoreDetails = loadable(
  async () => {
    return await import('./component/machineHealth/MaintenanceScoreDetails');
  },
  {fallback: <Suspense fallback="Loading"> </Suspense>}
);

const DocumentCapturePage = loadable(
  async () => {
    return await import('./component/documentCapture/DocumentCapturePage');
  },
  {fallback: <Suspense fallback="Loading"> </Suspense>}
);

const DigiDiaryLanding = loadable(
  async () => {
    await loadModuleTxFile('digidiary');
    return await import(
      './component/DigiDiary/digiDiaryLanding/digiDiaryLanding'
    );
  },
  {fallback: <Suspense fallback="Loading"> </Suspense>}
);

const Login = loadable(
  async () => {
    return await import(
      /* webpackChunkName: "Login" */ './component/login/Login'
    );
  },
  {fallback: <Suspense fallback="Loading"> </Suspense>}
);
const SignOut = loadable(
  async () => {
    return await import(
      /* webpackChunkName: "signOut" */ './component/signOut/signOut'
    );
  },
  {fallback: <Suspense fallback="Loading"> </Suspense>}
);

const Oops = loadable(
  async () => {
    return await import(/* webpackChunkName: "Oops" */ './component/oops/Oops');
  },
  {fallback: <Suspense fallback="Loading"> </Suspense>}
);

const ResalePreviousEnquiryList = loadable(
  async () => {
    return await import(
      /* webpackChunkName: "ResalePreviousEnquiryList" */ './component/resale/resalePreviousEnquiryList'
    );
  },
  {fallback: <Suspense fallback="Loading"> </Suspense>}
);
const ResaleItemDetails = loadable(
  async () => {
    return await import(
      /* webpackChunkName: "ResaleItemDetails" */ './component/resale/resaleItemDetails'
    );
  },
  {fallback: <Suspense fallback="Loading"> </Suspense>}
);
const ResaleSubmitEnquiry = loadable(
  async () => {
    return await import(
      /* webpackChunkName: "ResaleSubmitEnquiry" */ './component/resale/resaleEnquiry'
    );
  },
  {fallback: <Suspense fallback="Loading"> </Suspense>}
);
const PreviousSubmittedOffersListLanding = loadable(
  async () => {
    return await import(
      /* webpackChunkName: "PreviousSubmittedOffersListLanding" */ './component/offers/outside_mfa_offers_landing/PreviousSubmittedOffers'
    );
  },
  {fallback: <Suspense fallback="Loading"> </Suspense>}
);
const NonMfaOffer = loadable(
  async () => {
    return await import(
      /* webpackChunkName: "NonMfaOffer" */ './component/offers/outside_mfa_offers_landing/NonMfaOffer'
    );
  },
  {fallback: <Suspense fallback="Loading"> </Suspense>}
);
const NonMFASubmitForm = loadable(
  async () => {
    return await import(
      /* webpackChunkName: "NonMFASubmitForm" */ './component/MobileVerification/NonMFASubmitForm'
    );
  },
  {fallback: <Suspense fallback="Loading"> </Suspense>}
);
const LandingPage = loadable(
  async () => {
    return await import(/* webpackChunkName: "LandingPage" */ './LandingPage');
  },
  {fallback: <Suspense fallback="Loading"> </Suspense>}
);
const PreApprovedOffersLandingPage = loadable(
  async () => {
    return await import(
      './component/offers/inside_mfa_offers_landing/preApprovedOffersLanding'
    );
  },
  {fallback: <Suspense fallback="Loading"> </Suspense>}
);

const MFAOffersEnquiry = loadable(
  async () => {
    return await import(
      './component/offers/inside_mfa_offers_landing/offersEnquiry'
    );
  },
  {fallback: <Suspense fallback="Loading"> </Suspense>}
);

const newRelicScript = `js/new_relic_${process.env.REACT_APP_ENVIRONMENT}.js`;

export const oktaAuth = new OktaAuth({
  issuer: oktaConfig.issuer,
  clientId: oktaConfig.clientId,
  redirectUri: oktaConfig.redirectUri,
});

export const HasAccessToBrowserRouter = () => {
  const [isResaleTractorOn, setIsResaleTractorOn] = React.useState(false);
  const [isMachineHealthOn, setIsMachineHealthOn] = React.useState(false);
  const enableDebugLogging = useFlag('enableDebugLogging');
  const resaleFeatureFlag = useFlag('resale');
  const machineHealthFeatureFlag = useFlag('shouldShowMachineHealth');
  React.useEffect(() => {
    setIsResaleTractorOn(resaleFeatureFlag);
  }, [resaleFeatureFlag]);
  React.useEffect(() => {
    setIsMachineHealthOn(machineHealthFeatureFlag);
  }, [machineHealthFeatureFlag]);
  React.useEffect(() => {
    setDebugLogging(enableDebugLogging);
  }, [enableDebugLogging]);
  const navigate = useNavigate();
  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    navigate(toRelativeUrl('/', window.location.origin));
  };

  const [isFAQ, setIsFAQ] = React.useState(null);
  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <ErrorBoundary>
        <Routes>
          <Route path="/" element={<SecureRoute />}>
            <Route
              exact
              path=""
              element={<LandingPage oktaAuth={oktaAuth} />}
            />
            <Route
              exact
              path="/digiDiary"
              element={<DigiDiaryLanding oktaAuth={oktaAuth} />}
            />
            {isMachineHealthOn && (
              <Route
                exact
                path="/machineHealth"
                element={<MachineHealth oktaAuth={oktaAuth} />}
              />
            )}
            <Route
              exact
              path="/mfaPreviousSubmittedOffers"
              element={<MfaPreviousOffersLanding />}
            />
            {isMachineHealthOn && (
              <Route
                exact
                path="/machineInsights"
                element={<MachineInsightsDetails oktaAuth={oktaAuth} />}
              />
            )}
            {isMachineHealthOn && (
              <Route
                exact
                path="/usageScore"
                element={<UsageScoreDetails oktaAuth={oktaAuth} />}
              />
            )}
            {isMachineHealthOn && (
              <Route
                exact
                path="/maintenanceScore"
                element={<MaintenanceScoreDetails oktaAuth={oktaAuth} />}
              />
            )}
            <Route
              exact
              path="/document-capture"
              element={<DocumentCapturePage />}
            />
          </Route>
          <Route path="/login" element={<Login oktaAuth={oktaAuth} />} />
          <Route path="/signOut" element={<SignOut />} />
          <Route path="/oops" element={<Oops />} />
          <Route path="/oktalogin" element={<LoginCallback />} />
          <Route path="/resetOrgPassword" element={<SetOrgPassword />} />
          <Route exact path="/offerslist" element={<OffersLanding />} />
          {isResaleTractorOn && (
            <Route
              exact
              path="/resale"
              element={<ResaleLanding oktaAuth={oktaAuth}></ResaleLanding>}
            />
          )}
          <Route exact path="/offerslist/:offerId" element={<NonMfaOffer />} />
          <Route
            exact
            path="/resale-asset/:assetId"
            element={<ResaleItemDetails oktaAuth={oktaAuth} />}
          />
          <Route
            exact
            path="/previous-resale-enquiry"
            element={<ResalePreviousEnquiryList oktaAuth={oktaAuth} />}
          />
          <Route
            path="/locked"
            element={
              !isFAQ ? (
                <LockedUserPage setIsFAQ={setIsFAQ} />
              ) : (
                <FAQ page="lockedUserPage" setIsFAQ={setIsFAQ} />
              )
            }
          />
          <Route path="/verifyMobile" element={<MobileVerification />} />
          <Route path="/nonMFASubmitOffer" element={<NonMFASubmitForm />} />
          <Route path="/resale-enquiry" element={<ResaleSubmitEnquiry />} />
          <Route
            path="/previousSubmittedOffers"
            element={<PreviousSubmittedOffersListLanding />}
          />
          <Route
            path="/preApprovedOffers"
            element={<PreApprovedOffersLandingPage />}
          />
          <Route
            path="/enquirySubmittedSuccess"
            element={<NonMfaSubmittedEnquiry />}
          />
          <Route path="/offerEnquiry/:offerId" element={<MFAOffersEnquiry />} />
        </Routes>
      </ErrorBoundary>
    </Security>
  );
};

const putDeviceTokenAPI = async (deviceToken, deleteToken = false) => {
  const checkAuth = await oktaAuth.isAuthenticated();
  if (checkAuth) {
    const {preferred_username} = await oktaAuth.getUser();
    await postAPICall(
      'deviceToken?customerId=' + preferred_username,
      {
        deviceToken: deviceToken,
        deleteToken: deleteToken,
      },
      {
        errorTag: 'SET_DEVICE_TOKEN',
        logInfoMsg: 'customer device token set successfully ',
      }
    );
  }
};

const App = () => {
  const [isSmartlookInitialised, setIsSmartlookInitialised] =
    React.useState(false);
  const maintenancePageFeatureFlag = useFlag('maintenancePage');
  const maintenancePageFeaturePayload = useVariant('maintenancePage').payload;
  const getFcmToken = React.useCallback((data) => {
    if (data.data) {
      try {
        let tempData;
        if (typeof data.data === 'string') {
          tempData = JSON.parse(data.data);
        } else {
          tempData = data.data;
        }
        const tempToken = tempData.fcmtoken;
        const otp = tempData.login_otp;
        if (tempData?.reactivatedFcmToken) {
          putDeviceTokenAPI(tempData.reactivatedFcmToken);
        }
        if (tempData?.deleteUserToken) {
          putDeviceTokenAPI(tempData.token, true);
        }
        if (tempToken && !localStorage.getItem('fcmtoken')) {
          localStorage.setItem('fcmtoken', tempToken);
        } else if (otp && !localStorage.getItem('loginOtp')) {
          appStore.dispatch(otpAction(otp));
        }
      } catch (err) {
        log.error('Error parsing FCM token message:', err);
      }
    }
  }, []);

  React.useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) {
        return;
      }
      getFcmToken(event);
    };

    window.addEventListener('message', handleMessage);
    document.addEventListener('message', getFcmToken);

    return () => {
      window.removeEventListener('message', handleMessage);
      document.removeEventListener('message', getFcmToken);
    };
  }, [getFcmToken]);

  //below lines of code added to avoid open feedback popoup automatically on route change.
  window.addEventListener('qsi_js_loaded', interceptLoaded, false);
  function interceptLoaded() {
    if (QSI.API) {
      QSI.API.unload();
    }
  }

  const [isProdDown, setIsProdDown] = React.useState(false);

  const [prodDownTimeValue, setprodDownTimeValue] = React.useState();
  const [isOffline, setIsOffline] = React.useState(false);
  const [offlineModalVisibility, setOfflineModalVisibility] =
    React.useState(false);

  const renderOfflinePopup = () => (
    <CustomModal
      className="offlineModal"
      title={isOffline ? 'You are offline' : 'You are online'}
      subTitle={
        isOffline ? 'Please check yor network' : 'Your network is restored'
      }
      hideCloseButton={true}
      primaryButtonText={'OK'}
      primaryClickHandler={() => setOfflineModalVisibility(false)}
      isVisible={offlineModalVisibility}
    />
  );

  React.useEffect(() => {
    if (!isSmartlookInitialised) {
      initializeSmartlook();
      setIsSmartlookInitialised(true);
    }
  }, []);
  React.useEffect(() => {
    setIsProdDown(maintenancePageFeatureFlag);
    const downTime = maintenancePageFeaturePayload;
    downTime
      ? setprodDownTimeValue(downTime.value.toLowerCase())
      : setprodDownTimeValue('');
  }, [maintenancePageFeatureFlag, maintenancePageFeaturePayload]);

  const getPreservedState = () => {
    return new Promise((res, rej) => {
      try {
        let userId = sessionStorage.getItem('preserveI');
        let contractData = sessionStorage.getItem('preserveII');
        let customerDetailsData = sessionStorage.getItem('preserveIII');
        let userName = sessionStorage.getItem('preserveIV');
        let machineHealthDetails = sessionStorage.getItem('preserveV');
        if (userId && userId !== 'undefined' && userId !== 'null') {
          appStore.dispatch(userIdAction(JSON.parse(userId)));
          sessionStorage.removeItem('preserveI');
        }
        if (
          contractData &&
          contractData !== 'undefined' &&
          contractData !== 'null'
        ) {
          appStore.dispatch(contractSummaryAction(JSON.parse(contractData)));
          sessionStorage.removeItem('preserveII');
        }
        if (
          customerDetailsData &&
          customerDetailsData !== 'undefined' &&
          customerDetailsData !== 'null'
        ) {
          appStore.dispatch(
            customerDetailsAction(JSON.parse(customerDetailsData))
          );
          sessionStorage.removeItem('preserveIII');
        }
        if (userName && userName !== 'undefined' && userName !== 'null') {
          appStore.dispatch(userNameAction(JSON.parse(userName)));
          sessionStorage.removeItem('preserveIV');
        }
        if (
          machineHealthDetails &&
          machineHealthDetails !== 'undefined' &&
          machineHealthDetails !== 'null'
        ) {
          appStore.dispatch(
            machineHealthDetailsAction(JSON.parse(machineHealthDetails))
          );
          sessionStorage.removeItem('preserveV');
        }
        res();
      } catch (e) {
        rej(e);
      }
    });
  };
  React.useEffect(() => {
    const preservedState = async () => {
      await getPreservedState();
    };
    preservedState();
  }, []);
  React.useEffect(() => {
    window.addEventListener('online', () => {
      setIsOffline(false);
      setOfflineModalVisibility(true);
    });
    window.addEventListener('offline', () => {
      setIsOffline(true);
      setOfflineModalVisibility(true);
    });
    window.addEventListener('beforeunload', () => {
      sessionStorage.setItem('preserveI', JSON.stringify(states?.userId));
      sessionStorage.setItem(
        'preserveII',
        JSON.stringify(states?.contractData)
      );
      sessionStorage.setItem(
        'preserveIII',
        JSON.stringify(states?.customerDetailsData)
      );
      sessionStorage.setItem('preserveIV', JSON.stringify(states?.userName));
      sessionStorage.setItem(
        'preserveV',
        JSON.stringify(states?.allMachineHealthDetails?.machineHealthDetails)
      );
    });
    setIsProdDown(maintenancePageFeatureFlag);
    const downTime = maintenancePageFeaturePayload;
    downTime
      ? setprodDownTimeValue(downTime.value.toLowerCase())
      : setprodDownTimeValue('');
  });

  const changeLanguage = async () => {
    await i18n.changeLanguage(
      localStorage.getItem('mfalancode')
        ? localStorage.getItem('mfalancode')
        : 'en'
    );
  };
  React.useEffect(() => {
    changeLanguage();
  }, []);

  if (isProdDown) {
    return (
      <div id="maintenance">
        <Maintenance data_time={prodDownTimeValue} />
      </div>
    );
  }
  return (
    <div id="app" className={isOffline && 'offlineView'}>
      <Suspense fallback="Loading">
        <Helmet>
          <script type="text/javascript" src={newRelicScript}></script>
        </Helmet>
        <BrowserRouter>
          <HasAccessToBrowserRouter />
        </BrowserRouter>
        {/* <InstallPrompt /> */}
        {renderOfflinePopup()}
      </Suspense>
    </div>
  );
};

export default App;
